// Pool Capacity Card — emergent feature (E2). Dashboard Compliance section.

import { Tooltip } from '../common/Tooltip';
import { fmtShares, pct } from '../../format/format';

type PoolCapacity = {
  poolShares: number;
  fdShares: number;
  poolPctFd: number;
  outstandingAwards: number;
  avgVestingMonths: number | null;
  monthsOfCapacity: number | null;
};

interface PoolCapacityCardProps {
  capacity: PoolCapacity | null;
}

export function PoolCapacityCard({ capacity }: PoolCapacityCardProps) {
  if (!capacity) {
    return (
      <div className="card">
        <h3>
          Option Pool Capacity
          <Tooltip
            label="Remaining authorized-but-unissued option pool, expressed as a share of fully-diluted shares and as months of run-rate capacity based on outstanding awards and their average vesting horizon."
            ariaLabel="Help: Option Pool Capacity"
          />
        </h3>
        <p className="field-hint">No active cap table to assess pool capacity.</p>
      </div>
    );
  }

  const lowCapacity =
    capacity.poolPctFd < 0.1 ||
    (capacity.monthsOfCapacity !== null && capacity.monthsOfCapacity < 12);

  return (
    <div className="card">
      <h3>
        Option Pool Capacity
        <Tooltip
          label="Remaining authorized-but-unissued option pool, expressed as a share of fully-diluted shares and as months of run-rate capacity based on outstanding awards and their average vesting horizon."
          ariaLabel="Help: Option Pool Capacity"
        />
      </h3>
      <div className="metric-grid-tight">
        <div className="metric">
          <span className="metric-label">Pool % of FD</span>
          <span className="metric-value">{pct(capacity.poolPctFd, 1)}</span>
        </div>
        <div className="metric">
          <span className="metric-label">Remaining Shares</span>
          <span className="metric-value">{fmtShares(capacity.poolShares)}</span>
        </div>
        <div className="metric">
          <span className="metric-label">Months of Capacity</span>
          <span className="metric-value">
            {capacity.monthsOfCapacity !== null ? capacity.monthsOfCapacity.toFixed(0) : '—'}
          </span>
        </div>
        <div className="metric">
          <span className="metric-label">Outstanding Awards</span>
          <span className="metric-value">{fmtShares(capacity.outstandingAwards)}</span>
        </div>
      </div>
      {lowCapacity && (
        <p className="badge badge-warning mt-3">
          Option pool is running low — consider expanding authorization before the next grant cycle.
        </p>
      )}
    </div>
  );
}
